import { createApiClient, type ApiClient } from '@planetra/api-client';
import type { ConnectivityStore } from './connectivity';
import { createConnectivityStore } from './connectivity';
import { createCsrfRefresher, readCsrfToken } from './csrf';
import { classifyApiError, isAbort, RETRY_AFTER_KEY } from './errors';
import { backoffMs, delay, shouldRetry } from './retry';

/**
 * The portal's API client (P0-WEB-04).
 *
 * `createApiClient` is a typed fetch and deliberately nothing more — see its
 * docblock in packages/api-client. Everything a browser session needs on top of
 * it lives here, in the app, because it needs the locale, the selected
 * organisation and somewhere to send a signed-out user.
 *
 * What it adds, in the order a request meets it:
 *
 *  1. `X-XSRF-TOKEN` from the cookie, without which every write is a 419.
 *  2. `X-Organisation-Id` from one getter, so no call site can forget it.
 *  3. `Accept-Language`, so the API can localise when it learns how to.
 *  4. A 419 retried once behind a fresh CSRF cookie — a handshake being
 *     completed, not an error being papered over.
 *  5. Backoff retries for reads only (see retry.ts).
 *  6. Connectivity tracking for the offline banner.
 *  7. A one-shot `onUnauthenticated` when a 401 arrives.
 *
 * What it does **not** do is navigate. Routing belongs to the route: a loader
 * catches the failure and `throw redirect(...)`, which is React Router's own
 * idiom and keeps the transport free of a router reference it would otherwise
 * need at module scope, where the router does not exist yet.
 *
 * Errors are rethrown as they came — `ApiError`, `TypeError`, `AbortError`.
 * Callers pass them to `classifyApiError`. Wrapping them in a bespoke error
 * class here would cost the original stack and buy nothing the classifier does
 * not already give.
 */

export interface PortalClientOptions {
  /** Platform API origin, no trailing slash. */
  readonly baseUrl: string;
  readonly fetch?: typeof globalThis.fetch;
  /**
   * The organisation every request acts for (P0-PLAT-10). Read per request
   * rather than captured, so switching organisations takes effect on the next
   * request without rebuilding the client.
   */
  readonly getOrganisationId?: () => string | undefined;
  /** BCP 47 tag for `Accept-Language`. */
  readonly getLocale?: () => string;
  /**
   * Called once when a 401 arrives, and not again until a request succeeds.
   *
   * A port, in the style of `observability/error-reporter.ts`: P0-WEB-07 wires
   * it to the auth store, and until then the default does nothing. The latch is
   * what makes it usable — a workspace with five loaders in flight produces five
   * 401s when a session is revoked, and clearing the store five times is
   * harmless while showing five notices is not.
   */
  readonly onUnauthenticated?: (revoked: boolean) => void;
  readonly connectivity?: ConnectivityStore;
  /** Tests only — makes backoff deterministic. */
  readonly random?: () => number;
  readonly sleep?: (ms: number) => Promise<void>;
}

export interface PortalClient {
  readonly request: ApiClient;
  readonly connectivity: ConnectivityStore;
  /**
   * Mints the CSRF cookie if the browser does not already hold one.
   *
   * The documented Sanctum flow is `/sanctum/csrf-cookie` and *then* the write.
   * The 419 replay above makes that optional rather than required — but a
   * first sign-in without it is a guaranteed 419, and a login endpoint whose
   * error rate is 50% by design is one nobody can monitor. Call it before the
   * first write of a session; everything after that already has the cookie.
   */
  readonly prepareCsrf: () => Promise<void>;
}

export function createPortalClient(options: PortalClientOptions): PortalClient {
  const {
    baseUrl,
    fetch: fetchImpl = globalThis.fetch.bind(globalThis),
    getOrganisationId,
    getLocale,
    onUnauthenticated,
    connectivity = createConnectivityStore(),
    random = Math.random,
    sleep = delay,
  } = options;

  const refreshCsrf = createCsrfRefresher(baseUrl, fetchImpl);

  // Latched so one revoked session produces one reaction, not one per request
  // in flight. Cleared by any success, because a request that worked means the
  // session is good again — after signing back in, the next 401 is new news.
  let signalledUnauthenticated = false;

  const instrumentedFetch: typeof globalThis.fetch = async (input, init) => {
    const method = (init?.method ?? 'GET').toUpperCase();

    // Counted separately from the CSRF retry below. Folding them into one
    // counter would let a stale token silently spend the budget a flapping
    // gateway needs — and the two have nothing to do with each other.
    let attempt = 0;
    let csrfRetried = false;

    for (;;) {
      let response: Response;

      try {
        response = await fetchImpl(input, { ...init, headers: buildHeaders(init) });
      } catch (error) {
        // An abort is the caller's decision, not a transport failure. Retrying
        // it would resurrect a request the caller has already walked away from.
        if (isAbort(error)) throw error;

        connectivity.markUnreachable();
        if (!shouldRetry({ method, status: undefined, attempt })) throw error;

        await sleep(backoffMs(attempt, random));
        attempt++;
        continue;
      }

      // Something answered, even if it answered badly.
      connectivity.markReachable();

      // 419 is Laravel saying the CSRF token is stale — typically because the
      // session was regenerated on login, or the tab sat open past the cookie's
      // lifetime. Once, for any method: replaying the write is safe because the
      // rejected one provably never ran.
      if (response.status === 419 && !csrfRetried) {
        csrfRetried = true;
        await refreshCsrf();
        continue;
      }

      if (response.status === 429) return await withRetryAfter(response);

      if (!shouldRetry({ method, status: response.status, attempt })) return response;

      await sleep(backoffMs(attempt, random));
      attempt++;
    }
  };

  const inner = createApiClient({ baseUrl, fetch: instrumentedFetch });

  const request: ApiClient = async (path, method, requestOptions) => {
    try {
      const result = await inner(path, method, requestOptions);
      signalledUnauthenticated = false;
      return result;
    } catch (error) {
      const failure = classifyApiError(error);

      if (failure.kind === 'unauthenticated' && !signalledUnauthenticated) {
        signalledUnauthenticated = true;
        onUnauthenticated?.(failure.revoked);
      }

      throw error;
    }
  };

  return {
    request,
    connectivity,
    async prepareCsrf() {
      // Skipped when a cookie is already held: the refresher is single-flight
      // but not free, and minting a new token invalidates the one any request
      // in flight is about to send.
      if (readCsrfToken() !== undefined) return;
      await refreshCsrf();
    },
  };

  function buildHeaders(init: RequestInit | undefined): Headers {
    const headers = new Headers(init?.headers);

    const token = readCsrfToken();
    if (token !== undefined) headers.set('X-XSRF-TOKEN', token);

    // Set only when absent, so the per-call `organisationId` on
    // ApiRequestOptions still wins — a member list for an organisation other
    // than the selected one is a legitimate request.
    if (!headers.has('X-Organisation-Id')) {
      const organisationId = getOrganisationId?.();
      if (organisationId !== undefined && organisationId !== '') {
        headers.set('X-Organisation-Id', organisationId);
      }
    }

    const locale = getLocale?.();
    if (locale !== undefined && locale !== '') headers.set('Accept-Language', locale);

    return headers;
  }
}

/**
 * Folds `Retry-After` into the body.
 *
 * `ApiError` forwards the status and the parsed body but not the headers, and
 * packages/api-client is generated — the alternative to this is a second,
 * hand-written fetch path for one status code. See `RETRY_AFTER_KEY`.
 */
async function withRetryAfter(response: Response): Promise<Response> {
  const header = response.headers.get('Retry-After');
  if (header === null) return response;

  // The header is either delta-seconds or an HTTP date; Laravel's throttle
  // sends seconds. A date parses as NaN here and is simply dropped, which
  // degrades to "try again later" rather than "try again in NaN seconds".
  const seconds = Number.parseInt(header, 10);
  if (!Number.isFinite(seconds)) return response;

  // Re-serialised rather than passed through: the body has to be read to merge
  // into it, and a Response body can only be read once.
  const text = await response.text();

  return new Response(mergeRetryAfter(text, seconds), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

function mergeRetryAfter(text: string, seconds: number): string {
  try {
    const parsed: unknown = text === '' ? {} : JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return text;
    return JSON.stringify({ ...parsed, [RETRY_AFTER_KEY]: seconds });
  } catch {
    // A proxy's HTML rate-limit page. Left alone so it reaches the classifier
    // as an unparseable body rather than becoming a JSON error here.
    return text;
  }
}
